/** Mirrors `backend/src/game/shop/shop.types.ts` (the Atelier half). */
import type { EquipmentSlot, ItemStatsSummary } from './game-state.types';
import type { ShopCatalogEntry } from './shop-state.types';

/** The rail's tabs, in the order the rail draws them. */
export const ATELIER_TABS = ['HATS', 'JACKETS', 'VESTS', 'TROUSERS', 'GLOVES', 'BOOTS', 'SETS'] as const;
export type AtelierTab = (typeof ATELIER_TABS)[number];

/** A single garment card: a catalog row from `?storefront=TAILOR`. */
export type AtelierEntry = ShopCatalogEntry & { owned: boolean };

export interface AtelierBundlePiece {
  itemKey: string;
  name: string;
  slot: EquipmentSlot | null;
  iconPath: string | null;
  stats: ItemStatsSummary;
  owned: boolean;
  /** The single price, or null for a set-only piece (the golden ones). */
  singlePrice: number | null;
}

export interface AtelierBundle {
  key: string;
  name: string;
  goldPrice: number;
  storefront: string;
  category: string;
  sortOrder: number;
  offerEndsAt: string | null;
  pieces: AtelierBundlePiece[];
  /** Struck through on the card. Null when any piece is set-only. */
  singlesSum: number | null;
  /** Shown BEFORE paying: "You already own N of these". */
  piecesOwned: number;
  /** Bonus `statMods.flat` while every piece is worn. */
  bonus: Record<string, number>;
}

/** One piece on the fitting stage. Never equipped: see `fitPiece`. */
export interface FitPiece {
  itemKey: string;
  name: string;
  slot: EquipmentSlot;
  iconPath: string | null;
  stats: ItemStatsSummary;
}

/** What just happened, for the one-shot animations and Hollis's speech tag. */
export interface AtelierEvent {
  kind: 'fit' | 'sold' | 'set' | 'short' | 'sewing';
  key: string;
  /** Monotonic id, so the same event twice still re-triggers. */
  seq: number;
}

export interface AtelierState {
  catalog: AtelierEntry[] | null;
  bundles: AtelierBundle[] | null;
  tab: AtelierTab;
  /** Pieces being tried on, by slot. Lives HERE, never in `gameStateStore`. */
  fit: Partial<Record<EquipmentSlot, FitPiece>>;
  /** Before/After: true shows the survivor as they are, without the fitted pieces. */
  showBefore: boolean;
  busy: boolean;
  error: string | null;
  receipt: string | null;
  event: AtelierEvent | null;
}
